import React from "react";
import Header from "parts/Header";
import Footer from "parts/Footer";
import Discuss from "parts/Discuss";
// eslint-disable-next-line object-curly-newline
import { Fade, Slide, Zoom, AttentionSeeker } from "react-awesome-reveal";

const TargetMarketSegmentPage = () => (
  <>
    <Header />

    {/* Hero Section with Gradient Background */}
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-800">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute bg-blue-400 rounded-full top-10 left-10 w-72 h-72 mix-blend-multiply filter blur-xl animate-pulse" />
        <div className="absolute delay-300 bg-purple-400 rounded-full top-20 right-10 w-96 h-96 mix-blend-multiply filter blur-xl animate-pulse" />
        <div className="absolute delay-500 bg-indigo-400 rounded-full bottom-10 left-1/2 w-80 h-80 mix-blend-multiply filter blur-xl animate-pulse" />
      </div>

      <div className="container relative px-6 py-20 mx-auto">
        <Fade direction="up" triggerOnce duration={1000}>
          <div className="text-center text-white">
            <AttentionSeeker effect="pulse" triggerOnce>
              <h1 className="mb-6 text-6xl font-extrabold text-transparent md:text-7xl bg-gradient-to-r from-white to-blue-200 bg-clip-text">
                Target Market
              </h1>
            </AttentionSeeker>
            <Fade direction="up" delay={300} triggerOnce>
              <h2 className="mb-8 text-3xl font-light text-blue-100 md:text-4xl">
                Segment Analysis
              </h2>
            </Fade>
            <Fade direction="up" delay={600} triggerOnce>
              <p className="max-w-4xl mx-auto text-xl leading-relaxed text-gray-200 md:text-2xl">
                Empowering diverse businesses with strategic solutions for
                venture funding, compliance excellence, and robust
                infrastructure development
              </p>
            </Fade>
          </div>
        </Fade>
      </div>
    </div>

    {/* Main Content */}
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Market Segments Grid */}
      <div className="container px-6 py-20 mx-auto">
        <Fade direction="up" triggerOnce>
          <h3 className="mb-16 text-4xl font-bold text-center text-gray-800">
            Who We Serve
          </h3>
        </Fade>

        <div className="grid gap-8 mb-20 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: "Real Estate Developers",
              icon: "🏢",
              description:
                "Strategic funding and compliance solutions for property development projects",
              color: "from-emerald-400 to-teal-600",
            },
            {
              title: "Startups",
              icon: "🚀",
              description:
                "End-to-end support from ideation to market entry and scaling",
              color: "from-purple-400 to-pink-600",
            },
            {
              title: "SMEs",
              icon: "🎯",
              description:
                "Growth-oriented solutions for small and medium enterprises",
              color: "from-blue-400 to-indigo-600",
            },
            {
              title: "Corporates",
              icon: "🏛️",
              description:
                "Enterprise-level infrastructure and compliance management",
              color: "from-orange-400 to-red-600",
            },
            {
              title: "International Firms",
              icon: "🌍",
              description:
                "Cross-border expertise for global business expansion",
              color: "from-cyan-400 to-blue-600",
            },
            {
              title: "Growth Partners",
              icon: "📈",
              description:
                "Venture funding and strategic partnership facilitation",
              color: "from-green-400 to-emerald-600",
            },
          ].map((segment, index) => (
            <Slide
              key={segment.title}
              direction={index % 2 === 0 ? "left" : "right"}
              delay={index * 100}
              triggerOnce
            >
              <div className="relative overflow-hidden transition-all duration-500 transform bg-white shadow-lg group rounded-2xl hover:shadow-2xl hover:-translate-y-2">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${segment.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                />
                <div className="relative p-8">
                  <div className="mb-4 text-4xl transition-transform duration-300 transform group-hover:scale-110">
                    {segment.icon}
                  </div>
                  <h4 className="mb-3 text-xl font-bold text-gray-800 group-hover:text-gray-900">
                    {segment.title}
                  </h4>
                  <p className="text-gray-600 group-hover:text-gray-700">
                    {segment.description}
                  </p>
                  <div
                    className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${segment.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}
                  />
                </div>
              </div>
            </Slide>
          ))}
        </div>

        {/* Featured Image Section */}
        <Zoom triggerOnce>
          <div className="max-w-5xl mx-auto mb-20">
            <div className="relative overflow-hidden shadow-2xl rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80"
                alt="Target Market Segment Analysis"
                className="object-cover w-full transition-transform duration-700 transform h-96 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute text-white bottom-8 left-8">
                <h4 className="mb-2 text-2xl font-bold">
                  Strategic Market Focus
                </h4>
                <p className="text-lg text-gray-200">
                  Precision-targeted solutions for maximum impact
                </p>
              </div>
            </div>
          </div>
        </Zoom>

        {/* Value Proposition */}
        <Fade direction="up" triggerOnce>
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative p-12 overflow-hidden bg-white shadow-xl rounded-3xl">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
              <h3 className="mb-8 text-3xl font-bold text-gray-800">
                Why Choose Our Approach?
              </h3>
              <div className="grid gap-8 md:grid-cols-3">
                <Fade direction="up" delay={200} triggerOnce>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-blue-400 to-purple-600">
                      1
                    </div>
                    <h4 className="mb-2 font-semibold text-gray-800">
                      Tailored Solutions
                    </h4>
                    <p className="text-sm text-gray-600">
                      Custom strategies designed for your specific growth phase
                      and market position
                    </p>
                  </div>
                </Fade>
                <Fade direction="up" delay={400} triggerOnce>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-purple-400 to-pink-600">
                      2
                    </div>
                    <h4 className="mb-2 font-semibold text-gray-800">
                      Expert Resources
                    </h4>
                    <p className="text-sm text-gray-600">
                      Access to specialized knowledge and industry expertise for
                      competitive advantage
                    </p>
                  </div>
                </Fade>
                <Fade direction="up" delay={600} triggerOnce>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-pink-400 to-red-600">
                      3
                    </div>
                    <h4 className="mb-2 font-semibold text-gray-800">
                      Proven Success
                    </h4>
                    <p className="text-sm text-gray-600">
                      Track record of helping businesses navigate complex
                      markets and achieve sustainable growth
                    </p>
                  </div>
                </Fade>
              </div>
            </div>
          </div>
        </Fade>

        {/* Call to Action */}
        <Fade direction="up" delay={800} triggerOnce>
          <div className="mt-20 text-center">
            <AttentionSeeker effect="heartBeat" delay={2000} triggerOnce>
              <div className="inline-block p-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                <button
                  type="button"
                  className="px-8 py-4 font-bold text-gray-800 transition-colors duration-300 transform bg-white rounded-full hover:bg-gray-50 hover:scale-105"
                >
                  Discover Your Growth Potential
                </button>
              </div>
            </AttentionSeeker>
          </div>
        </Fade>
      </div>
    </div>

    <Discuss />
    <Footer />
  </>
);

export default TargetMarketSegmentPage;
